/**
 * La feuille du bas.
 *
 * Elle monte du bas sur téléphone et devient une carte centrée au bureau —
 * une feuille pleine largeur sur un écran de 1400 px n'est plus une feuille,
 * c'est une bannière. Le basculement est entièrement dans la feuille de style ;
 * ce composant ne connaît qu'une chose, la structure voile + panneau.
 *
 * Ce qu'elle prend en charge et qu'un `<div>` ne prendrait pas :
 *
 *   — Échap ferme, partout, sans que chaque appelant y pense ;
 *   — le focus part dans le panneau à l'ouverture et revient d'où il venait à
 *     la fermeture, sinon la tabulation reprend en haut du document ;
 *   — le voile est un vrai `<button>`, pas un `<div onClick>` : cliquer à côté
 *     pour fermer doit exister aussi au clavier.
 *
 * Le défilement du fond n'est pas verrouillé : la coque ne défile pas sur
 * téléphone (voir `app.css`), et `overscroll-behavior: contain` sur le panneau
 * suffit à empêcher le geste de traverser.
 */

import { useEffect, useRef, type ReactNode } from 'react';

interface SheetProps {
  onClose: () => void;
  /** Nommée pour les lecteurs d'écran : c'est un dialogue, il lui faut un nom. */
  label: string;
  children: ReactNode;
}

export function Sheet({ onClose, label, children }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);

  // Déplacer le focus, et le rendre. Une seule fois par ouverture : ce cycle
  // est lié à la vie de la feuille, pas à l'identité de `onClose`.
  useEffect(() => {
    const previous = document.activeElement;

    // Le panneau lui-même reçoit le focus, pas son premier bouton : ouvrir une
    // fiche ne doit pas donner l'air qu'on s'apprête à appeler quelqu'un.
    panel.current?.focus({ preventScroll: true });

    return () => {
      if (previous instanceof HTMLElement) previous.focus({ preventScroll: true });
    };
  }, []);

  // Échap ferme. Effet séparé, qui suit `onClose` : se réabonner quand le
  // rappel change coûte moins qu'une référence mutée pendant le rendu.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={label}>
      <button
        type="button"
        className="sheet-veil"
        aria-label="Fermer"
        onClick={onClose}
      />
      <div className="sheet-panel" ref={panel} tabIndex={-1}>
        <div className="sheet-grip" aria-hidden="true" />
        {children}
      </div>
    </div>
  );
}
